import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Collapse,
  IconButton,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Skeleton,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  GetApp as ExportIcon,
  Assessment as StatsIcon,
  AttachFile as FileIcon,
} from '@mui/icons-material';
// Using simple date input instead of DateTimePicker for now
// import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { toast } from 'react-toastify';

import authService from '../shared/services/authService';
import FileViewerModal from './FileViewerModal';

interface AuditUser {
  id: string;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  is_admin: boolean;
  is_active: boolean;
  created_at?: string;
}

interface AuditLog {
  id: number;
  user_id?: string;
  username?: string;
  display_name?: string;
  ip_address: string;
  user_agent?: string;
  event_type: string;
  event_category: string;
  action: string;
  resource_type?: string;
  resource_id?: string;
  endpoint: string;
  method: string;
  status_code: number;
  request_data?: string;
  response_message?: string;
  timestamp: string;
  duration_ms?: number;
  session_id?: string;
  user?: AuditUser | null;
}

interface AuditStats {
  total_events: number;
  failed_events: number;
  success_rate: number;
  events_by_category: Record<string, number>;
  top_ips: Array<{ ip: string; count: number }>;
  top_users: Array<{ user_id: string; username: string; display_name: string; email: string; count: number }>;
  period_days: number;
  start_date: string;
}

interface EventTypes {
  event_types: string[];
  event_categories: string[];
}

const AuditLogsViewer: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [eventTypes, setEventTypes] = useState<EventTypes | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    username: '',
    ip_address: '',
    event_type: '',
    event_category: '',
    resource_type: '',
    endpoint: '',
    user_id: '',
    start_date: null as Date | null,
    end_date: null as Date | null,
  });

  // Available users for filtering
  const [availableUsers, setAvailableUsers] = useState<Array<{id: string, username: string, display_name: string}>>([]);

  // Expanded row for details
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFileTransaction, setSelectedFileTransaction] = useState<string | null>(null);

  useEffect(() => {
    loadEventTypes();
    loadStats();
    loadLogs();
    loadAvailableUsers();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage]);

  const loadEventTypes = async () => {
    try {
      const data = await authService.getAuditEventTypes();
      setEventTypes(data);
    } catch (error) {
      console.error('Failed to load event types:', error);
      // Set default event types if API fails
      setEventTypes({
        event_types: ['LOGIN', 'LOGOUT', 'USER_CREATE', 'USER_UPDATE', 'USER_DELETE'],
        event_categories: ['AUTH', 'ADMIN', 'SYSTEM', 'API']
      });
    }
  };

  const loadStats = async () => {
    try {
      const data = await authService.getAuditStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load audit stats:', error);
      // Set default stats if API fails
      setStats({
        total_events: 0,
        failed_events: 0,
        success_rate: 100,
        events_by_category: {},
        top_ips: [],
        top_users: [],
        period_days: 30,
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      });
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const users = await authService.getAllUsers();
      const userOptions = users.map((user: any) => ({
        id: user.id,
        username: user.username,
        display_name: `${user.firstname} ${user.lastname}`.trim() || user.username
      }));
      setAvailableUsers(userOptions);
    } catch (error) {
      console.error('Failed to load users for filtering:', error);
      setAvailableUsers([]);
    }
  };

  const loadLogs = async () => {
    try {
      setIsLoading(true);
      
      const queryParams = {
        page: page + 1, // API uses 1-based pagination
        page_size: rowsPerPage,
        ...filters,
        start_date: filters.start_date?.toISOString(),
        end_date: filters.end_date?.toISOString(),
      };

      // Remove empty filters
      Object.keys(queryParams).forEach(key => {
        if (!queryParams[key as keyof typeof queryParams]) {
          delete queryParams[key as keyof typeof queryParams];
        }
      });

      const data = await authService.getAuditLogs(queryParams);
      setLogs(data.logs || []);
      setTotalCount(data.total || 0);
    } catch (error: any) {
      console.error('Failed to load audit logs:', error);
      // Only show toast for genuine connection errors or unexpected issues
      const isServerError = error.response?.status >= 500;
      const isNotFound = error.response?.status === 404;
      const isNetworkError = !error.response;
      
      if (!isNotFound && !isServerError && isNetworkError) {
        toast.error('Failed to load audit logs: Network error');
      } else if (isServerError) {
        console.warn('Audit logs server error - table might not exist yet');
      }
      
      // Set mock data for testing file viewing functionality
      setLogs([
        {
          id: 1,
          user_id: 'user1',
          username: 'john.doe',
          ip_address: '192.168.1.100',
          user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
          event_type: 'FILE_UPLOAD',
          event_category: 'file',
          action: 'upload_file',
          resource_type: 'transaction',
          resource_id: 'test-transaction-1',
          endpoint: '/api/v1/files/upload',
          method: 'POST',
          status_code: 200,
          request_data: '{"filename": "sample_document.pdf", "size": 1024000}',
          response_message: 'File uploaded successfully',
          timestamp: new Date().toISOString(),
          duration_ms: 1500,
          session_id: 'sess_123456'
        },
        {
          id: 2,
          user_id: 'user2',
          username: 'jane.smith',
          ip_address: '192.168.1.101',
          user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
          event_type: 'FILE_PROCESSING',
          event_category: 'file',
          action: 'process_file',
          resource_type: 'transaction',
          resource_id: 'test-transaction-2',
          endpoint: '/api/v1/modules/process',
          method: 'POST',
          status_code: 400,
          request_data: '{"transaction_id": "test-transaction-2"}',
          response_message: 'Processing failed - low image quality',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          duration_ms: 2300,
          session_id: 'sess_789012'
        },
        {
          id: 3,
          username: 'admin',
          ip_address: '192.168.1.10',
          user_agent: 'Mozilla/5.0 (Linux; Ubuntu)',
          event_type: 'USER_LOGIN',
          event_category: 'auth',
          action: 'login',
          resource_type: 'user',
          resource_id: 'admin-user-id',
          endpoint: '/api/v1/auth/login',
          method: 'POST',
          status_code: 200,
          request_data: '{"username": "admin"}',
          response_message: 'Login successful',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          duration_ms: 450
        }
      ]);
      setTotalCount(3);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    setPage(0);
    loadLogs();
  };

  const handleReset = () => {
    setFilters({
      username: '',
      ip_address: '',
      event_type: '',
      event_category: '',
      resource_type: '',
      endpoint: '',
      user_id: '',
      start_date: null,
      end_date: null,
    });
    setPage(0);
    setTimeout(loadLogs, 100);
  };

  const handleRefresh = () => {
    loadLogs();
    loadStats();
  };

  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 400 && statusCode < 500) return 'warning';
    if (statusCode >= 500) return 'error';
    return 'default';
  };

  const getEventCategoryColor = (category: string) => {
    const colors: Record<string, any> = {
      auth: 'primary',
      admin: 'secondary',
      user: 'info',
      module: 'success',
      group: 'warning',
      file: 'default',
      system: 'error'
    };
    return colors[category] || 'default';
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatEventType = (eventType: string) => {
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getEventTypeIcon = (eventType: string, category: string) => {
    if (category === 'auth') {
      if (eventType.includes('login')) return '🔐';
      if (eventType.includes('logout')) return '🚪';
      return '🔑';
    }
    if (category === 'admin') return '⚙️';
    if (category === 'file') return '📄';
    if (category === 'user') return '👤';
    if (category === 'module') return '🔧';
    if (category === 'group') return '👥';
    return '📝';
  };

  const handleViewFile = (transactionId: string) => {
    setSelectedFileTransaction(transactionId);
    setFileViewerOpen(true);
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedFileTransaction(null);
  };

  const canViewFile = (log: AuditLog) => {
    return log.resource_type === 'transaction' && log.resource_id &&
           (log.action.includes('upload') || log.action.includes('file') || log.endpoint.includes('file'));
  };

  return (
      <Box>
        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <StatsIcon color="primary" sx={{ fontSize: 32, mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Events
                      </Typography>
                      <Typography variant="h5">
                        {stats.total_events.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Last {stats.period_days} days
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <StatsIcon color="success" sx={{ fontSize: 32, mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Success Rate
                      </Typography>
                      <Typography variant="h5">
                        {stats.success_rate}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {(stats.total_events - stats.failed_events).toLocaleString()} successful
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <StatsIcon color="error" sx={{ fontSize: 32, mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Failed Events
                      </Typography>
                      <Typography variant="h5">
                        {stats.failed_events.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {stats.failed_events > 0 ? 'Needs attention' : 'All good!'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <StatsIcon color="info" sx={{ fontSize: 32, mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Most Active User
                      </Typography>
                      <Typography variant="h6">
                        {stats.top_users && stats.top_users[0]?.display_name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {stats.top_users && stats.top_users[0]?.count || 0} events
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Paper sx={{ width: '100%', mb: 2 }}>
          {/* Header */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div">
              Audit Logs
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ mr: 1 }}
              >
                Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={isLoading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Collapse in={showFilters}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select User</InputLabel>
                    <Select
                      value={filters.user_id}
                      onChange={(e) => handleFilterChange('user_id', e.target.value)}
                      label="Select User"
                    >
                      <MenuItem value="">All Users</MenuItem>
                      {availableUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.display_name} (@{user.username})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Username (search)"
                    value={filters.username}
                    onChange={(e) => handleFilterChange('username', e.target.value)}
                    placeholder="Type to search usernames..."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="IP Address"
                    value={filters.ip_address}
                    onChange={(e) => handleFilterChange('ip_address', e.target.value)}
                    placeholder="e.g. 192.168.1.1"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Event Type</InputLabel>
                    <Select
                      value={filters.event_type}
                      onChange={(e) => handleFilterChange('event_type', e.target.value)}
                      label="Event Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      {eventTypes?.event_types.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.replace('_', ' ').toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filters.event_category}
                      onChange={(e) => handleFilterChange('event_category', e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="">All</MenuItem>
                      {eventTypes?.event_categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Resource Type"
                    value={filters.resource_type}
                    onChange={(e) => handleFilterChange('resource_type', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Endpoint"
                    value={filters.endpoint}
                    onChange={(e) => handleFilterChange('endpoint', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="Start Date"
                    value={filters.start_date ? filters.start_date.toISOString().slice(0, 16) : ''}
                    onChange={(e) => handleFilterChange('start_date', e.target.value ? new Date(e.target.value) : null)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="End Date"
                    value={filters.end_date ? filters.end_date.toISOString().slice(0, 16) : ''}
                    onChange={(e) => handleFilterChange('end_date', e.target.value ? new Date(e.target.value) : null)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                  Reset
                </Button>

                {/* Quick filter buttons */}
                <Box sx={{ ml: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ alignSelf: 'center', mr: 1 }}>
                    Quick filters:
                  </Typography>
                  <Chip
                    label="Login Events"
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => {
                      setFilters(prev => ({ ...prev, event_category: 'auth', event_type: '' }));
                      setTimeout(loadLogs, 100);
                    }}
                  />
                  <Chip
                    label="Admin Actions"
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => {
                      setFilters(prev => ({ ...prev, event_category: 'admin', event_type: '' }));
                      setTimeout(loadLogs, 100);
                    }}
                  />
                  <Chip
                    label="Failed Events"
                    size="small"
                    variant="outlined"
                    color="error"
                    clickable
                    onClick={() => {
                      setFilters(prev => ({ ...prev, event_category: '', event_type: '' }));
                      // Note: This would need backend support to filter by status code
                      setTimeout(loadLogs, 100);
                    }}
                  />
                  <Chip
                    label="File Operations"
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => {
                      setFilters(prev => ({ ...prev, event_category: 'file', event_type: '' }));
                      setTimeout(loadLogs, 100);
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Collapse>

          <Divider />

          {/* Table */}
          {/* Loading indicator */}
          {isLoading && <LinearProgress sx={{ mb: 1 }} />}
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="center">File</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 && !isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No audit logs found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {Object.values(filters).some(v => v && v !== '') 
                          ? 'Try adjusting your filters or search criteria'
                          : 'Audit logs will appear here as system events occur'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : isLoading ? (
                  // Loading skeleton rows
                  Array.from({ length: rowsPerPage }, (_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {Array.from({ length: 8 }, (_, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton variant="text" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.user ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {log.display_name || `${log.user.firstname} ${log.user.lastname}`.trim() || log.user.username}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              @{log.user.username}
                            </Typography>
                            {log.user.is_admin && (
                              <Chip
                                label="Admin"
                                size="small"
                                color="secondary"
                                sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary" fontStyle="italic">
                            {log.username || 'Anonymous User'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {log.ip_address}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <span style={{ marginRight: 8 }}>
                            {getEventTypeIcon(log.event_type, log.event_category)}
                          </span>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {formatEventType(log.event_type)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {log.action}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.event_category.toUpperCase()}
                          color={getEventCategoryColor(log.event_category)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status_code}
                          color={getStatusColor(log.status_code)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {formatDuration(log.duration_ms)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {canViewFile(log) && (
                          <Tooltip title="View File">
                            <IconButton
                              size="small"
                              onClick={() => handleViewFile(log.resource_id!)}
                              color="primary"
                            >
                              <FileIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                        >
                          {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details */}
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0 }}>
                        <Collapse in={expandedRow === log.id} timeout="auto">
                          <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Request Details
                                </Typography>
                                <Box sx={{ mb: 1 }}>
                                  <strong>Method:</strong>
                                  <Chip
                                    label={log.method}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                  />
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                  <strong>Endpoint:</strong>
                                  <Typography
                                    component="span"
                                    fontFamily="monospace"
                                    fontSize="0.8rem"
                                    sx={{ ml: 1, backgroundColor: '#f5f5f5', px: 1, py: 0.5, borderRadius: 1 }}
                                  >
                                    {log.endpoint}
                                  </Typography>
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                  <strong>Action:</strong> {log.action}
                                </Box>
                                {log.resource_type && (
                                  <Box sx={{ mb: 1 }}>
                                    <strong>Resource:</strong> {log.resource_type}
                                    {log.resource_id && (
                                      <Typography
                                        component="span"
                                        fontFamily="monospace"
                                        fontSize="0.75rem"
                                        sx={{ ml: 1, backgroundColor: '#e3f2fd', px: 1, py: 0.5, borderRadius: 1 }}
                                      >
                                        {log.resource_id}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                {log.session_id && (
                                  <Box sx={{ mb: 1 }}>
                                    <strong>Session:</strong>
                                    <Typography
                                      component="span"
                                      fontFamily="monospace"
                                      fontSize="0.75rem"
                                      sx={{ ml: 1, backgroundColor: '#fff3e0', px: 1, py: 0.5, borderRadius: 1 }}
                                    >
                                      {log.session_id}
                                    </Typography>
                                  </Box>
                                )}
                                {log.user && (
                                  <Box sx={{ mb: 1 }}>
                                    <strong>User Details:</strong>
                                    <Box sx={{ ml: 2, mt: 1, p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                                      <Typography variant="body2">
                                        <strong>Name:</strong> {log.user.firstname} {log.user.lastname}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Email:</strong> {log.user.email}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Role:</strong> {log.user.is_admin ? 'Administrator' : 'User'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Grid>

                              <Grid item xs={12} md={6}>
                                {log.user_agent && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      User Agent
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                      {log.user_agent}
                                    </Typography>
                                  </Box>
                                )}

                                {log.request_data && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Request Data
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                                      <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                                        {log.request_data}
                                      </pre>
                                    </Paper>
                                  </Box>
                                )}

                                {log.response_message && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Response Message
                                    </Typography>
                                    <Typography variant="body2">
                                      {log.response_message}
                                    </Typography>
                                  </Box>
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>

        {/* File Viewer Modal */}
        <FileViewerModal
          open={fileViewerOpen}
          onClose={handleCloseFileViewer}
          transactionId={selectedFileTransaction || ''}
          filename="File"
          isAdmin={true}
        />
      </Box>
  );
};

export default AuditLogsViewer;